'use client';

import { useState, useEffect } from 'react';
import { Button } from '@heroui/react';
import { FaArrowRotateLeft, FaXmark } from 'react-icons/fa6';
import {
  loadDraftFromStorage,
  clearDraftFromStorage,
} from '../utils/draftStorage';
import { useScheduler } from '../SchedulerContext';
import { useStartOver } from '../hooks/useStartOver';
import { useDraftQuotes } from '../hooks/useDraftQuotes';
import DraftSelectionModal from './DraftSelectionModal';
import { Quote } from '@/util/types/serverTypes';
import { callServerAction } from '@/util/functions/fetching/callServerAction';

export default function ResumeDraftBanner() {
  const {
    draftQuoteSlug,
    setDraftQuoteSlug,
    shouldResumeDraft,
    setShouldResumeDraft,
    draftQuoteId,
    setDraftQuoteId,
    setContact,
    setSelectedRole,
    jwtToken,
    companyId,
    contact,
    startNewQuote,
  } = useScheduler();
  const [showBanner, setShowBanner] = useState(false);
  const [storedContactId, setStoredContactId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Use centralized quote fetching hook
  const { quotes, isLoading, fetchQuotes, clearQuotes } = useDraftQuotes(
    jwtToken,
    companyId
  );

  // Get startOver function with full reset
  const handleStartOver = useStartOver({
    navigateToStep: 1,
    clearDraft: true,
    deferNavigation: true,
  });

  useEffect(() => {
    // Don't show banner if we're actively resuming a draft
    // This prevents the banner from showing when user selects "Continue Editing"
    if (shouldResumeDraft) {
      setShowBanner(false);
      setStoredContactId(null);
      return;
    }

    // Check if there's a stored contactId that's different from current contact
    const storedSession = loadDraftFromStorage();

    if (
      storedSession &&
      storedSession.contactId &&
      storedSession.contactId !== contact?._id
    ) {
      // There's a stored contactId that we're not currently using
      setStoredContactId(storedSession.contactId);
      setShowBanner(true);
    } else if (
      !storedSession ||
      !storedSession.contactId ||
      storedSession.contactId === contact?._id
    ) {
      // No stored contactId or it matches current contact - hide banner
      setShowBanner(false);
      setStoredContactId(null);
    }
  }, [contact?._id, shouldResumeDraft]);

  const handleResume = async () => {
    if (!storedContactId || isProcessing) return;

    setIsProcessing(true);
    try {
      // Fetch quotes for this contact
      await fetchQuotes(storedContactId);
      // Quotes state will be updated by the hook
      // Check quotes in useEffect below
    } catch (error) {
      console.error('[ResumeDraftBanner] Error fetching quotes:', error);
      // Hide banner on error
      setShowBanner(false);
      setIsProcessing(false);
    }
  };

  // Open modal when quotes are fetched and available
  useEffect(() => {
    // Only process if we're in the middle of fetching (isProcessing is true)
    if (!isProcessing) return;

    // Wait for loading to complete
    if (isLoading) return;

    // After loading completes, check results
    if (quotes.length > 0) {
      // Quotes found - open modal
      setShowQuoteModal(true);
      setIsProcessing(false);
    } else {
      // No quotes found - hide banner
      setShowBanner(false);
      setIsProcessing(false);
    }
  }, [quotes, isLoading, isProcessing, showQuoteModal]);

  // Handler for when user selects an existing draft quote
  const handleSelectDraft = (quote: Quote) => {
    // console.log('[ResumeDraftBanner] handleSelectDraft called:', {
    //   quoteId: quote._id,
    //   slug: quote.slug,
    //   status: quote.status,
    // });
    setShowQuoteModal(false);
    setShowBanner(false);
    // Set draft slug and resume flag - DraftLoader will handle loading the draft data and navigation
    // console.log('[ResumeDraftBanner] Setting draftQuoteSlug and shouldResumeDraft...');
    setDraftQuoteSlug(quote.slug);
    setShouldResumeDraft(true);
  };

  // Handler for when user wants to start new quote from draft quote modal
  const handleStartNew = async () => {
    // console.log('[ResumeDraftBanner] handleStartNew called, clearing old draft state');
    setShowQuoteModal(false);
    setShowBanner(false);

    // CRITICAL: Clear old draft state BEFORE starting new quote
    // This prevents auto-save from updating the old draft
    if (draftQuoteId) {
      // console.log('[ResumeDraftBanner] Clearing old draftQuoteId:', draftQuoteId);
      setDraftQuoteId(null);
      setDraftQuoteSlug(null);
      setShouldResumeDraft(false);
    }

    // Clear localStorage
    clearDraftFromStorage();
    // Clear quotes cache
    clearQuotes();
    // Start new quote (this will also clear draft state, but we do it here too for safety)
    await startNewQuote();
  };

  const handleStartFresh = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      // Determine which contactId to use for deleting quotes
      const contactIdToUse = storedContactId || contact?._id;

      // Delete only draft quotes for this contact (not abandoned)
      if (contactIdToUse && jwtToken && companyId) {
        try {
          // Fetch only draft quotes (not abandoned) for this contact
          const quotesToDelete = await callServerAction<Quote[]>({
            url: 'quote',
            method: 'get',
            params: {
              contactId: contactIdToUse,
              status: 'draft', // Only fetch draft quotes, not abandoned
              populated: 'true',
              _companyId: companyId,
              limit: 20,
            },
            headers: {
              'client-token': jwtToken,
            },
          });

          // Delete each draft quote individually
          if (quotesToDelete && quotesToDelete.length > 0) {
            const deletePromises = quotesToDelete.map((quote) =>
              callServerAction({
                url: `quote/${quote._id}`,
                method: 'delete',
                params: {
                  _companyId: companyId,
                },
                headers: {
                  'client-token': jwtToken,
                },
              }).catch((error) => {
                // Log but don't fail - some quotes might already be deleted
                console.warn(
                  `[ResumeDraftBanner] Failed to delete quote ${quote._id}:`,
                  error
                );
              })
            );

            await Promise.all(deletePromises);
            console.log(
              `[ResumeDraftBanner] Deleted ${quotesToDelete.length} draft quote(s) for contact ${contactIdToUse}`
            );
          }
        } catch (error) {
          console.error(
            '[ResumeDraftBanner] Error fetching/deleting draft quotes:',
            error
          );
          // Continue even if deletion fails
        }
      }

      // Clear localStorage
      clearDraftFromStorage();
      // Clear quotes cache
      clearQuotes();
      // Clear contact and role
      setContact(null);
      setSelectedRole(null);
      // Start over
      handleStartOver();

      setShowBanner(false);
      console.log('[ResumeDraftBanner] Started fresh - all data cleared');
    } catch (error) {
      console.error('[ResumeDraftBanner] Error starting fresh:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Don't clear storage - user might want to resume later
  };

  if (!showBanner || !storedContactId) {
    return null;
  }

  return (
    <>
      <div className='relative border-b border-warning-300 bg-warning-50'>
        <div className='mx-auto max-w-7xl px-3 py-2.5 sm:px-6 sm:py-3 lg:px-8'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-start gap-2.5 sm:items-center sm:gap-3'>
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-warning-200 sm:h-10 sm:w-10'>
                <FaArrowRotateLeft className='text-warning-800' size={16} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-warning-900 sm:text-base'>
                  You have an unfinished booking
                </p>
                <p className='mt-0.5 text-xs text-warning-700 sm:text-sm'>
                  Would you like to continue where you left off?
                </p>
              </div>
            </div>

            <div className='flex items-center gap-2 sm:shrink-0'>
              <Button
                size='sm'
                color='warning'
                onPress={handleResume}
                startContent={<FaArrowRotateLeft size={14} />}
                isDisabled={isProcessing || isLoading}
                isLoading={isLoading}
                className='flex-1 sm:flex-initial'
              >
                Resume
              </Button>
              <Button
                size='sm'
                variant='flat'
                color='default'
                onPress={handleStartFresh}
                isDisabled={isProcessing}
                className='flex-1 sm:flex-initial'
              >
                Start Fresh
              </Button>
              <Button
                size='sm'
                variant='light'
                color='default'
                onPress={handleDismiss}
                isIconOnly
                isDisabled={isProcessing}
                className='shrink-0'
              >
                <FaXmark size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Draft Selection Modal */}
      {showQuoteModal && (
        <DraftSelectionModal
          drafts={quotes}
          isOpen={showQuoteModal}
          onSelectDraft={handleSelectDraft}
          onStartNew={handleStartNew}
          onClose={() => {
            setShowQuoteModal(false);
            // Don't clear cache or localStorage on close
          }}
          currentDraftId={draftQuoteId}
        />
      )}
    </>
  );
}
